import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ChevronDown,
  FileText,
  Heart,
  MapPin,
  Search,
  ShieldCheck,
  Users,
  Zap,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Marquee from "@/components/Marquee";
import CampaignCard from "@/components/CampaignCard";
import ProgressBar from "@/components/ProgressBar";
import CountUp from "@/components/CountUp";
import { fetchCampaigns, formatBRL, IMAGES, MAIN_SLUG } from "@/lib/api";

const HEADLINE = ["Cuidar de quem não", "tem voz é o nosso", "maior sonho."];

const TRUST = [
  {
    icon: ShieldCheck,
    title: "Doação 100% segura",
    text: "Pagamento Pix processado pela LivePix com criptografia de ponta a ponta.",
  },
  {
    icon: Zap,
    title: "Pix instantâneo",
    text: "QR Code e checkout gerados na hora, do R$ 1 ao R$ 500.",
  },
  {
    icon: FileText,
    title: "Transparência total",
    text: "Prestação de contas mensal do abrigo, aberta a todos os apoiadores.",
  },
];

const FAQ = [
  {
    q: "Qual o valor mínimo de doação?",
    a: "A partir de R$ 1! Use a barra deslizante ou os botões rápidos (R$ 1, 5, 25, 50, 100 e 500). O limite por doação é de R$ 500.",
  },
  {
    q: "Como o pagamento funciona?",
    a: "Ao clicar em 'Doar agora via Pix', geramos um pagamento no checkout da LivePix: escaneie o QR Code ou abra o link no app do seu banco.",
  },
  {
    q: "Meu dinheiro vai direto para o abrigo?",
    a: "Sim. Os valores caem na conta do Instituto PetLovers e viram ração, cuidado veterinário, vacinas e obras do abrigo.",
  },
  {
    q: "Posso doar em nome de alguém?",
    a: "Pode! Preencha seu nome e uma mensagem de carinho — ela aparece na lista de apoiadores da campanha.",
  },
];

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.13, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay: index * 0.06 }}
      className="mb-3 overflow-hidden rounded-2xl border border-[#EFE9DF] bg-white"
    >
      <button
        data-testid="faq-item-btn"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 p-6 text-left"
      >
        <span className="font-display font-bold text-[#261C14]">{item.q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-[#FF6D47] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className="px-6 pb-6 text-[#6E6259]">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Home() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  useEffect(() => {
    fetchCampaigns()
      .then(setCampaigns)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = campaigns.find((c) => c.slug === MAIN_SLUG);
  const categories = useMemo(() => [...new Set(campaigns.map((c) => c.category))], [campaigns]);
  const locations = useMemo(() => [...new Set(campaigns.map((c) => c.location))], [campaigns]);
  const filtered = campaigns.filter(
    (c) =>
      (!q || c.title.toLowerCase().includes(q.toLowerCase())) &&
      (!category || c.category === category) &&
      (!location || c.location === location)
  );

  return (
    <div className="min-h-screen">
      <Header />

      {/* HERO */}
      <section ref={heroRef} className="bg-paw-dots relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-12 sm:px-6 lg:grid-cols-2 lg:pb-28 lg:pt-20">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#FFE2D9] bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-wider text-[#FF6D47] shadow-sm"
            >
              <Heart className="h-3.5 w-3.5 fill-[#FF6D47]" /> Instituto PetLovers · Abrigo pet
            </motion.span>

            <h1
              data-testid="hero-title"
              className="font-display mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-[#261C14] sm:text-5xl lg:text-6xl"
            >
              {HEADLINE.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span custom={i} variants={lineReveal} initial="hidden" animate="show" className="block">
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="mt-6 max-w-md text-base font-semibold text-[#6E6259] sm:text-lg"
            >
              Doe de R$ 1 a R$ 500 via Pix e transforme cada real em ração, vacina e uma segunda chance para centenas de
              patinhas.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to={`/vaquinha/${MAIN_SLUG}`}
                data-testid="hero-donate-cta"
                className="group flex h-14 items-center gap-2 rounded-full bg-[#FF6D47] px-8 text-base font-extrabold text-white shadow-[0_10px_30px_rgba(255,109,71,0.4)] transition hover:-translate-y-0.5 hover:bg-[#E85732]"
              >
                Fazer uma doação <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#vaquinhas"
                data-testid="hero-explore-cta"
                className="flex h-14 items-center rounded-full border-2 border-[#EFE9DF] bg-white px-8 text-base font-extrabold text-[#261C14] transition hover:border-[#FF6D47] hover:text-[#FF6D47]"
              >
                Explorar causas
              </a>
            </motion.div>

            {featured && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="mt-10 flex items-center gap-4"
              >
                <div className="flex -space-x-3">
                  {["MC", "RJ", "SM", "TB", "PH"].map((n, i) => (
                    <span
                      key={n}
                      className={`grid h-10 w-10 place-items-center rounded-full border-2 border-white text-xs font-extrabold text-white ${
                        ["bg-[#FF6D47]", "bg-[#1F4E3D]", "bg-[#F59E0B]", "bg-[#32BCAD]", "bg-[#E11D48]"][i]
                      }`}
                    >
                      {n}
                    </span>
                  ))}
                </div>
                <p className="text-sm font-bold text-[#6E6259]">
                  <span className="text-[#261C14]">{featured.supporters} apoiadores</span> já ajudaram
                  <br />
                  nesta campanha do abrigo
                </p>
              </motion.div>
            )}
          </div>

          <motion.div style={{ y: parallaxY }} className="relative hidden lg:block">
            <motion.img
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              src={IMAGES.hero}
              alt="Voluntária do abrigo com cães e gatos resgatados ao pôr do sol"
              className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-2xl shadow-[#FF6D47]/20"
            />
            {featured && (
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-6 rounded-2xl bg-white/95 p-5 shadow-xl backdrop-blur"
              >
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#9E9085]">Arrecadado no abrigo</p>
                <p className="font-display text-2xl font-extrabold text-[#FF6D47]">{formatBRL(featured.raised_cents)}</p>
                <ProgressBar pct={featured.progress} className="mt-2 w-40" />
              </motion.div>
            )}
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-5 right-8 flex items-center gap-2 rounded-full bg-white/95 px-4 py-2.5 shadow-lg"
            >
              <Heart className="h-4 w-4 fill-[#E11D48] text-[#E11D48]" />
              <span className="text-sm font-extrabold text-[#261C14]">{featured?.hearts ?? 342} corações</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.6 }}
        className="border-y border-[#EFE9DF] bg-white"
      >
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-3">
          {TRUST.map((t) => (
            <div key={t.title} className="flex gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#FFF0EB] text-[#FF6D47]">
                <t.icon className="h-6 w-6" />
              </span>
              <div>
                <h3 className="font-display font-bold text-[#261C14]">{t.title}</h3>
                <p className="mt-1 text-sm text-[#6E6259]">{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* SEARCH + GRID */}
      <section id="vaquinhas" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#FF6D47]">Vaquinhas mais amadas</p>
        <h2 className="font-display mt-2 text-3xl font-extrabold text-[#261C14] sm:text-4xl">
          Escolha uma causa para apoiar
        </h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          className="mt-8 grid gap-3 rounded-2xl border border-[#EFE9DF] bg-white p-4 shadow-sm md:grid-cols-[2fr_1fr_1fr]"
        >
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9E9085]" />
            <input
              data-testid="home-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar vaquinhas de pets..."
              className="h-12 w-full rounded-xl border border-[#EFE9DF] bg-[#F7F3ED] pl-11 pr-4 text-sm font-semibold outline-none transition focus:border-[#FF6D47] focus:bg-white"
            />
          </div>
          <div className="relative">
            <select
              data-testid="filter-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="h-12 w-full appearance-none rounded-xl border border-[#EFE9DF] bg-[#F7F3ED] px-4 text-sm font-bold text-[#261C14] outline-none transition focus:border-[#FF6D47]"
            >
              <option value="">Todas as categorias</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9E9085]" />
          </div>
          <div className="relative">
            <select
              data-testid="filter-location-select"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="h-12 w-full appearance-none rounded-xl border border-[#EFE9DF] bg-[#F7F3ED] px-4 text-sm font-bold text-[#261C14] outline-none transition focus:border-[#FF6D47]"
            >
              <option value="">Todas as localizações</option>
              {locations.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
            <MapPin className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9E9085]" />
          </div>
        </motion.div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {loading && [...Array(4)].map((_, i) => <div key={i} className="h-80 animate-pulse rounded-2xl bg-white" />)}
          {!loading && filtered.map((c, i) => <CampaignCard key={c.slug} campaign={c} index={i} />)}
          {!loading && !filtered.length && (
            <p className="col-span-full rounded-2xl bg-white p-10 text-center font-bold text-[#9E9085]">
              Nenhuma vaquinha encontrada com esses filtros.
            </p>
          )}
        </div>
      </section>

      {/* FEATURED SPOTLIGHT */}
      {featured && (
        <section className="mx-auto max-w-7xl px-4 pb-4 sm:px-6">
          <motion.div
            data-testid="featured-campaign-card"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="grid overflow-hidden rounded-[2rem] border border-[#FFE2D9] bg-white shadow-xl shadow-[#FF6D47]/5 lg:grid-cols-2"
          >
            <div className="relative min-h-64">
              <img src={featured.cover} alt={featured.title} className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <div className="flex flex-col justify-center gap-5 p-8 lg:p-12">
              <span className="w-fit rounded-full bg-[#FFF0EB] px-4 py-1.5 text-xs font-extrabold uppercase tracking-wider text-[#FF6D47]">
                Campanha em destaque
              </span>
              <h3 className="font-display text-2xl font-extrabold leading-snug text-[#261C14] sm:text-3xl">
                {featured.title}
              </h3>
              <ProgressBar pct={featured.progress} />
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <p className="font-display text-2xl font-extrabold text-[#FF6D47]">
                  {formatBRL(featured.raised_cents)}{" "}
                  <span className="text-base font-bold text-[#9E9085]">de {formatBRL(featured.goal_cents)}</span>
                </p>
                <span className="flex items-center gap-1.5 text-sm font-bold text-[#6E6259]">
                  <Heart className="h-4 w-4 fill-[#E11D48] text-[#E11D48]" /> {featured.hearts} corações
                </span>
                <span className="flex items-center gap-1.5 text-sm font-bold text-[#6E6259]">
                  <Users className="h-4 w-4" /> {featured.supporters} apoiadores
                </span>
              </div>
              <Link
                to={`/vaquinha/${featured.slug}`}
                data-testid="campaign-donate-btn"
                className="mt-2 flex h-14 w-fit items-center gap-2 rounded-full bg-[#FF6D47] px-8 text-base font-extrabold text-white shadow-[0_10px_30px_rgba(255,109,71,0.35)] transition hover:-translate-y-0.5 hover:bg-[#E85732]"
              >
                Quero doar agora <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </section>
      )}

      <div className="py-16 lg:py-20">
        <Marquee />
      </div>

      {/* IMPACT */}
      <section id="como-ajudar" className="border-y border-[#EFE9DF] bg-[#FFF0EB]/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#FF6D47]">O impacto de doar</p>
          <h2 className="font-display mt-2 max-w-2xl text-3xl font-extrabold text-[#261C14] sm:text-4xl">
            Cada real doado vira cuidado no abrigo
          </h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            {[
              { value: 4820, label: "pets resgatados desde 2019", format: (v) => `+${Math.round(v).toLocaleString("pt-BR")}` },
              {
                value: 18500,
                label: "kg de ração entregues",
                format: (v) => `${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} t`,
              },
              { value: 310, label: "pets acolhidos neste momento", format: (v) => Math.round(v).toLocaleString("pt-BR") },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-[#FFE2D9] bg-white p-8 shadow-sm">
                <CountUp value={s.value} format={s.format} className="font-display text-4xl font-extrabold text-[#FF6D47] sm:text-5xl" />
                <p className="mt-3 font-bold text-[#6E6259]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="seguranca" className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:py-20">
        <p className="text-center text-xs font-extrabold uppercase tracking-[0.2em] text-[#FF6D47]">Segurança e transparência</p>
        <h2 className="font-display mt-2 text-center text-3xl font-extrabold text-[#261C14] sm:text-4xl">
          Você está seguro no PetLovers
        </h2>
        <div className="mt-10">
          {FAQ.map((f, i) => (
            <FaqItem key={f.q} item={f} index={i} />
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
